import { useMemo, useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import EntityTable from "../../common/tables/EntityTable";
import { Compare } from "@emberly/zenith-client";
import { useNavigate, useParams } from "react-router-dom";
import ResponsivePage from "../../common/core/ResponsivePage";
import { Grid, Tabs, Tab } from "@mui/material";
import config from "../../../config";
import useContactFields from "../../../hooks/useContactFields";
import { useDevice } from "../../../providers/DeviceProvider";

const useStyles = makeStyles()(
  (theme) => ({
    tabs: {
      marginBottom: theme.spacing(3),
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    tabsSlim: {
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  })
);

const IsCustomerFilter = {
  name: "contact_isCustomer",
  value: true,
  path: "isCustomer",
  comparer: Compare.EQ
}

export default function ContactRegister() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tabId } = useParams();
  const { classes } = useStyles();
  const { slim } = useDevice();

  const columns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: "#",
        flex: 0.5,
        minWidth: 80
      },
      {
        field: "name",
        headerName: t("common:name"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "organizationNumber",
        headerName: t("entity:contact:organizationNumber"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "address",
        headerName: t("entity:location:address"),
        flex: 1,
        minWidth: 120,
        valueGetter: (_, row) => row.location?.address || "-",
      },
      {
        field: "email",
        headerName: t("common:email"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "phoneNumber",
        headerName: t("entity:contact:phoneNumber"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "description",
        headerName: t("entity:contact:description"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "customerNumber",
        headerName: t("entity:contact:customerNumber"),
        type: "number",
        flex: 1,
        minWidth: 120,
        valueGetter: (_, row) => row?.customerNumber,
        valueFormatter: value => value || ""
      },
      {
        field: "billingAddress",
        headerName: t("contactRegister:fieldBillingAddress"),
        flex: 1,
        minWidth: 120,
        valueGetter: (_, row) => row.billing.address || "-"
      },
      {
        field: "billingEmail",
        headerName: t("contactRegister:fieldBillingEmail"),
        flex: 1,
        minWidth: 120,
        valueGetter: (_, row) => row.billing.email || "-"
      },
    ]
  }, [t]);

  const fields = useContactFields();

  const onCreated = useCallback((entity) => {
    navigate(!!tabId ? `./${entity.id}/details` : `./all/${entity.id}/details`, { relative: "path" });
  }, [navigate, tabId]);

  const onRowClick = useCallback((params, ev) => {
    navigate(!!tabId ? `./${params.id}/details` : `./all/${params.id}/details`, { relative: "path" });
  }, [navigate, tabId]);

  const onTabChange = useCallback((_, val) => navigate(!!tabId ? `../${val}` : `./${val}`, { relative: "path", replace: true }), [navigate, tabId]);

  return (
    <ResponsivePage
      title={t("contactRegister:title")}
      maxWidth={config.pageSize.large}
    >

      <Grid item xs={12}>
        <Tabs className={slim ? classes.tabsSlim : classes.tabs} value={tabId || "all"} onChange={onTabChange} variant="scrollable" scrollButtons="auto">
          <Tab value="all" label={t("contactRegister:all")} />
          <Tab value="customers" label={t("contactRegister:customers")} />
        </Tabs>
      </Grid>

      {
        !tabId || tabId === "all" ? (
          <EntityTable
            type="Contact"
            tableId="contacts_all"
            columns={columns}
            fields={fields}
            createTitle={t("contactRegister:create")}
            editTitle={t("contactRegister:edit")}
            emptyStateTitle={t("contactRegister:emptyStateTitle")}
            emptyStateDescription={t("contactRegister:emptyStateDescription")}
            onRowClick={onRowClick}
            createButton
            onCreated={onCreated}
          />
        ) : tabId === "customers" ? (
          <EntityTable
            type="Contact"
            tableId="contacts_customers"
            columns={columns}
            fields={fields}
            filter={IsCustomerFilter}
            createTitle={t("contactRegister:create")}
            editTitle={t("contactRegister:edit")}
            emptyStateTitle={t("contactRegister:emptyStateTitle")}
            emptyStateDescription={t("contactRegister:emptyStateDescription")}
            onRowClick={onRowClick}
            createButton
            onCreated={onCreated}
          />
        ) : null
      }

    </ResponsivePage>
  );
}